import { useEffect, useState } from 'react'
import { Form, Select, TimePicker } from 'antd'
import { DAY_OPTIONS } from '@/pages/robots/robot-position-setup/constants'
import type { FormInstance } from 'antd'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { RobotFormValues } from '../RobotForm'
import dayjs from 'dayjs'

const PILL_TIME_PICKER =
  '!h-11 !w-full !rounded-full !border-[rgba(30,47,65,0.25)] !bg-white !px-4 !shadow-none hover:!border-[#1e2f41] focus-within:!border-[#1e2f41] focus-within:!shadow-[0_0_0_3px_rgba(30,47,65,0.18)] [&_.ant-picker-input>input]:!text-[#1f2937] [&_.ant-picker-input>input]:placeholder:!text-[#98a2b3] [&_.ant-picker-suffix]:!hidden'
const PILL_SELECT =
  '[&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!rounded-full [&_.ant-select-selector]:!border-[rgba(30,47,65,0.25)] [&_.ant-select-selector]:!px-4 [&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:hover:!border-[#1e2f41] [&.ant-select-focused_.ant-select-selector]:!border-[#1e2f41] [&.ant-select-focused_.ant-select-selector]:!shadow-[0_0_0_3px_rgba(30,47,65,0.18)] [&_.ant-select-selection-placeholder]:!text-[#98a2b3]'
const WORKING_DAY_SELECT = `${PILL_SELECT} h-11 !py-0 !items-center !bg-white !rounded-full [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!rounded-full [&_.ant-select-selection-overflow]:!flex-nowrap [&_.ant-select-selection-overflow]:!items-center [&_.ant-select-selection-item]:!me-0 [&_.ant-select-selection-item]:!h-full [&_.ant-select-selection-item]:!rounded-none [&_.ant-select-selection-item]:!bg-transparent [&_.ant-select-selection-item]:!px-0 [&_.ant-select-selection-item]:!font-medium [&_.ant-select-selection-item]:!text-[#1f2937] [&_.ant-select-selection-item]:!inline-flex [&_.ant-select-selection-item]:!items-center [&_.ant-select-selection-item-content]:!inline-flex [&_.ant-select-selection-item-content]:!items-center [&_.ant-select-selection-item-content]:!ml-[10px]`
const WORKING_DAY_OPTIONS = DAY_OPTIONS.map((option) => ({
  ...option,
  value: String(option.value),
}))

export interface WorkingSchedulesSectionProps {
  form: FormInstance<RobotFormValues>
  onWorkingScheduleAdd?: () => void
  onWorkingScheduleRemove?: (
    row: NonNullable<RobotFormValues['workingSchedules']>[number],
  ) => void
}

export function WorkingSchedulesSection({
  form,
  onWorkingScheduleAdd,
  onWorkingScheduleRemove,
}: WorkingSchedulesSectionProps) {
  const watchedSchedules = Form.useWatch('workingSchedules', form)
  const workingDaysSet = new Set(['1', '2', '3', '4', '5'])
  const weekendDaysSet = new Set(['6', '7'])
  const allDaysSet = new Set(['1', '2', '3', '4', '5', '6', '7'])
  const [rowErrors, setRowErrors] = useState<Record<number, string | null>>({})

  const isSameSet = (a: Set<string>, b: Set<string>): boolean => {
    if (a.size !== b.size) return false
    for (const item of a) {
      if (!b.has(item)) return false
    }
    return true
  }

  // Kiểm tra thời gian logic từng dòng
  useEffect(() => {
    const newErrors: Record<number, string | null> = {}
    if (watchedSchedules) {
      watchedSchedules.forEach((s: any, index: number) => {
        if (s.startTime && s.endTime) {
          const start = dayjs(s.startTime).format('HH:mm')
          const end = dayjs(s.endTime).format('HH:mm')
          if (end <= start) {
            newErrors[index] = 'Giờ kết thúc phải lớn hơn giờ bắt đầu'
          }
        }
      })
    }
    setRowErrors(newErrors)
  }, [watchedSchedules])

  const validateSchedules = (schedules: any[]) => {
    if (schedules.length === 0) return Promise.resolve()

    for (const s of schedules) {
      if (s.startTime && s.endTime && !s.endTime.isAfter(s.startTime)) {
        // Trả về reject để chặn submit
        return Promise.reject(new Error('INVALID_TIME_RANGE'))
      }
    }

    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        const s1 = schedules[i]
        const s2 = schedules[j]
        if (
          !s1.dayOfWeek ||
          !s2.dayOfWeek ||
          !s1.startTime ||
          !s2.startTime ||
          !s1.endTime ||
          !s2.endTime
        )
          continue

        const hasCommonDay = s1.dayOfWeek.some((d: string) =>
          s2.dayOfWeek.includes(d),
        )
        const isOverlapping =
          s1.startTime.isBefore(s2.endTime) && s2.startTime.isBefore(s1.endTime)

        if (hasCommonDay && isOverlapping) {
          return Promise.reject(
            new Error(
              'Các khung giờ đang bị trùng lặp, vui lòng kiểm tra lại.',
            ),
          )
        }
      }
    }

    return Promise.resolve()
  }

  return (
    <div>
      <span className="mb-2.5 block text-base font-semibold text-[#4a5565]">
        Thời gian làm việc <span className="text-red-500">*</span>
      </span>
      <Form.List
        name="workingSchedules"
        rules={[
          {
            validator: (_, value) => validateSchedules(value || []),
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.key} className="flex flex-col gap-1">
                <div className="flex items-start gap-3">
                  <Form.Item
                    name={[field.name, 'dayOfWeek']}
                    className="mb-0 w-[550px]"
                    rules={[
                      {
                        type: 'array',
                        min: 1,
                        required: true,
                        message: 'Chọn ngày',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Chọn ngày"
                      mode="multiple"
                      listHeight={360}
                      maxTagCount={0}
                      maxTagPlaceholder={(omittedValues) => {
                        const selectedValues = omittedValues
                          .map((v) => v.value)
                          .filter((v) => v !== undefined)
                          .map((v) => String(v))

                        const selectedSet = new Set(selectedValues)

                        if (isSameSet(selectedSet, allDaysSet)) {
                          return 'Tất cả các ngày trong tuần'
                        }

                        if (isSameSet(selectedSet, workingDaysSet)) {
                          return 'Ngày làm việc'
                        }
                        if (isSameSet(selectedSet, weekendDaysSet)) {
                          return 'Ngày cuối tuần'
                        }

                        if (omittedValues.length === 1) {
                          const firstLabel = omittedValues[0].label
                          return typeof firstLabel === 'string'
                            ? firstLabel
                            : String(firstLabel)
                        }

                        const labels = omittedValues
                          .map((v) => v.label)
                          .filter((l) => l !== undefined)
                          .map((l) => String(l))

                        return labels.join(', ')
                      }}
                      options={WORKING_DAY_OPTIONS}
                      className={WORKING_DAY_SELECT}
                    />
                  </Form.Item>

                  <Form.Item
                    name={[field.name, 'startTime']}
                    className="mb-0 flex-1"
                    rules={[{ required: true, message: 'Chọn giờ bắt đầu' }]}
                  >
                    <TimePicker format="HH:mm" className={PILL_TIME_PICKER} />
                  </Form.Item>

                  <Form.Item
                    name={[field.name, 'endTime']}
                    className="mb-0 flex-1"
                    rules={[{ required: true, message: 'Chọn giờ kết thúc' }]}
                  >
                    <TimePicker format="HH:mm" className={PILL_TIME_PICKER} />
                  </Form.Item>

                  <Button
                    variant="primary"
                    icon={<X size={14} />}
                    onClick={() => {
                      const row = form.getFieldValue([
                        'workingSchedules',
                        field.name,
                      ])
                      onWorkingScheduleRemove?.(row)
                      remove(field.name)
                    }}
                    disabled={fields.length === 1}
                    className={`mt-2 !h-8 !w-8 !p-0 ${fields.length === 1 ? '' : '!bg-[#DF4045]'}`}
                  />
                </div>
                {rowErrors[index] && (
                  <div className="text-red-500 text-xs px-2">
                    {rowErrors[index]}
                  </div>
                )}
              </div>
            ))}
            {errors.length > 0 &&
              errors.filter((error) => error !== 'INVALID_TIME_RANGE').length >
                0 && (
                <div className="text-red-500 text-sm font-medium">
                  {errors
                    .filter((error) => error !== 'INVALID_TIME_RANGE')
                    .map((error, index) => (
                      <div key={index}>
                        {error instanceof Error ? error.message : error}
                      </div>
                    ))}
                </div>
              )}
            <Button
              variant="outline"
              onClick={() => {
                add({
                  dayOfWeek: [],
                  startTime: undefined,
                  endTime: undefined,
                  source: 'created',
                  createdByAction: true,
                })
                onWorkingScheduleAdd?.()
              }}
              className="h-11 w-11 rounded-full border-1 border-[#ce7a58] text-[#ce7a58] hover:bg-[#fff7f3]"
            >
              <Plus size={18} />
            </Button>
          </div>
        )}
      </Form.List>
    </div>
  )
}
